// src/components/Logo.tsx
//
// Brand lockup for UnlimitedTests: the "Rising U" mark + wordmark.
//
// The mark is the brand initial — a "U" — whose right arm rises into an arrow:
// the name and the promise ("your score goes up") in one glyph. Hand-built
// inline SVG (no raster asset, scales with no layout shift, theme-styleable).
//
// Colour model:
//   - The U + the word "Unlimited" render in `currentColor` (the surrounding
//     foreground) so the lockup adapts to light/dark automatically.
//   - The rising arrow + the word "Tests" render in the emerald brand accent
//     (`--brand-fill`) — the one spot of colour.
//   - `onLight` pins fixed ink + emerald via inline styles, for surfaces that
//     are ALWAYS light regardless of theme (the white nav chip), where the
//     `.nav-gradient` white-text cascade would otherwise wash the mark out.

type Size = 'sm' | 'md' | 'lg'

type Props = {
  size?: Size
  /**
   * When true, renders only the mark (rising-U glyph) without the
   * "UnlimitedTests" wordmark. Use for tight footprints. The accessible name
   * remains "UnlimitedTests" so screen readers still announce the brand.
   */
  markOnly?: boolean
  className?: string
  /** Force ink + emerald (for always-white surfaces like the nav chip). */
  onLight?: boolean
}

const MARK_PX: Record<Size, number> = { sm: 22, md: 30, lg: 40 }
const WORDMARK_TEXT: Record<Size, string> = { sm: 'text-base', md: 'text-xl', lg: 'text-3xl' }

export default function Logo({ size = 'sm', markOnly = false, className, onLight = false }: Props) {
  const px = MARK_PX[size]
  const ink = onLight ? '#171717' : 'currentColor'
  const emerald = onLight ? '#059669' : 'hsl(var(--brand-fill))'

  return (
    <span
      className={`inline-flex items-center gap-2 leading-none ${className ?? ''}`}
      // Single accessible name for the whole lockup so screen readers hear
      // "UnlimitedTests" once, not "Unlimited, Tests". Children are aria-hidden.
      aria-label="UnlimitedTests"
      role="img"
    >
      <svg
        width={px}
        height={px}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        {/* the "U" */}
        <path
          d="M14 11 L14 27 C14 35 19 39.2 24 39.2 C29 39.2 34 35 34 27 L34 20.5"
          stroke={ink}
          strokeWidth="6.6"
          strokeLinecap="round"
          fill="none"
        />
        {/* the right arm rising into an arrow */}
        <path d="M34 21 L34 6" stroke={emerald} strokeWidth="6.6" strokeLinecap="round" />
        <path
          d="M27.4 12.6 L34 5.2 L40.6 12.6"
          stroke={emerald}
          strokeWidth="6.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>

      {!markOnly && (
        <span
          className={`font-extrabold tracking-tight ${WORDMARK_TEXT[size]}`}
          style={{ color: ink }}
        >
          Unlimited
          <span style={{ color: emerald }}>Tests</span>
        </span>
      )}
    </span>
  )
}
